import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Raycast Yapılandırma İnterface
 */
export interface RaycastConfig {
  meshThreshold: number;
  lineThreshold: number;
  pointsThreshold: number;
  spriteThreshold: number;
  lodThreshold: number;
}

/**
 * Önceden tanımlı Raycast Profilleri
 */
export const RAYCAST_PROFILES = {
  DEFAULT: {
    meshThreshold: 0.2,
    lineThreshold: 25.0,     // 🎯 15.0 → 25.0 (Çizgi seçimi daha da kolay)
    pointsThreshold: 2.0,    // Nokta seçimi düşük
    spriteThreshold: 0.1,
    lodThreshold: 0.3
  } as RaycastConfig,

  EVERYTHING: {
    meshThreshold: 0.5,
    lineThreshold: 30.0,     // 🎯 20.0 → 30.0 (Her şey modunda çok rahat çizgi seçim)
    pointsThreshold: 3.0,    // Nokta seçimi düşük
    spriteThreshold: 0.5,
    lodThreshold: 0.5
  } as RaycastConfig,

  POINTS_ONLY: {
    meshThreshold: 0,
    lineThreshold: 0,
    pointsThreshold: 5.0,    // Sadece nokta modunda makul alan
    spriteThreshold: 0,
    lodThreshold: 0
  } as RaycastConfig,

  LINES_ONLY: {
    meshThreshold: 0,
    lineThreshold: 40.0,     // 🎯 25.0 → 40.0 (Sadece çizgi modunda çok geniş alan)
    pointsThreshold: 0,
    spriteThreshold: 0,
    lodThreshold: 0
  } as RaycastConfig,

  MESH_ONLY: {
    meshThreshold: 1,
    lineThreshold: 0,
    pointsThreshold: 0,
    spriteThreshold: 0,
    lodThreshold: 0
  } as RaycastConfig,

  PRECISE: {
    meshThreshold: 0.2,
    lineThreshold: 0.5,
    pointsThreshold: 0.01,
    spriteThreshold: 0.1,
    lodThreshold: 0.3
  } as RaycastConfig,

  RELAXED: {
    meshThreshold: 0.2,
    lineThreshold: 0.2,
    pointsThreshold: 0.5,
    spriteThreshold: 0.1,
    lodThreshold: 0.3
  } as RaycastConfig,

  GRID_SNAP: {
    meshThreshold: 0,
    lineThreshold: 0.1,
    pointsThreshold: 0.1,
    spriteThreshold: 0,
    lodThreshold: 0
  } as RaycastConfig,

  SIMULATION: {
    meshThreshold: 0,
    lineThreshold: 0.1,
    pointsThreshold: 0,
    spriteThreshold: 0,
    lodThreshold: 0
  } as RaycastConfig
};

/**
 * Raycast yapılandırmasını merkezi olarak yönetir
 * Tüm seçim bileşenleri bu servisten ayarlarını alırlar
 */
@Injectable({
  providedIn: 'root'
})
export class FikoRaycastConfigService {

  private readonly STORAGE_KEY = 'FikoRaycastConfig';
  private readonly DEFAULT_PROFILE = 'DEFAULT';

  private configSubject = new BehaviorSubject<RaycastConfig>(RAYCAST_PROFILES.DEFAULT);
  public config$: Observable<RaycastConfig> = this.configSubject.asObservable();

  private currentProfileSubject = new BehaviorSubject<string>(this.DEFAULT_PROFILE);
  public currentProfile$: Observable<string> = this.currentProfileSubject.asObservable();

  private customConfigSubject = new BehaviorSubject<Map<string, RaycastConfig>>(new Map());
  public customConfigs$: Observable<Map<string, RaycastConfig>> = this.customConfigSubject.asObservable();

  constructor() {
    this.loadConfig();
  }

  /**
   * Önceden tanımlı profili uygula
   */
  applyProfile(profileName: keyof typeof RAYCAST_PROFILES): void {
    const profile = RAYCAST_PROFILES[profileName];
    if (profile) {
      this.setConfig(profile);
      this.currentProfileSubject.next(profileName);
      this.saveConfig();
    }
  }

  /**
   * Özel yapılandırma ayarla
   */
  setConfig(config: RaycastConfig): void {
    this.validateConfig(config);
    this.configSubject.next({ ...config });
    this.saveConfig();
  }

  /**
   * Mevcut yapılandırmayı al
   */
  getConfig(): RaycastConfig {
    return this.configSubject.value;
  }

  /**
   * Belirli bir threshold değerini güncelle
   */
  updateThreshold(type: keyof RaycastConfig, value: number): void {
    const config = this.getConfig();
    config[type] = Math.max(0, Math.min(10, value)); // 0-10 arasında sınırla
    this.setConfig(config);
  }

  /**
   * Raycast Params'i Three.js Raycaster'a uygun formatta al
   */
  getRaycastParams(): any {
    const config = this.getConfig();
    return {
      Mesh: { threshold: config.meshThreshold },
      Line: { threshold: config.lineThreshold },
      Points: { threshold: config.pointsThreshold },
      Sprite: { threshold: config.spriteThreshold },
      LOD: { threshold: config.lodThreshold }
    };
  }

  /**
   * Özel profil kaydet
   */
  saveCustomProfile(name: string, config: RaycastConfig): void {
    const customConfigs = this.customConfigSubject.value;
    customConfigs.set(name, { ...config });
    this.customConfigSubject.next(customConfigs);
    this.saveConfig();
  }

  /**
   * Özel profili sil
   */
  deleteCustomProfile(name: string): void {
    const customConfigs = this.customConfigSubject.value;
    customConfigs.delete(name);
    this.customConfigSubject.next(customConfigs);
    this.saveConfig();
  }

  /**
   * Özel profili uygula
   */
  applyCustomProfile(name: string): void {
    const customConfigs = this.customConfigSubject.value;
    const config = customConfigs.get(name);
    if (config) {
      this.setConfig(config);
      this.currentProfileSubject.next(`CUSTOM_${name}`);
    }
  }

  /**
   * Tüm profilleri al (standart + özel)
   */
  getAllProfiles(): { name: string; config: RaycastConfig }[] {
    const profiles: { name: string; config: RaycastConfig }[] = [];

    // Standart profilleri ekle
    Object.entries(RAYCAST_PROFILES).forEach(([name, config]) => {
      profiles.push({ name, config });
    });

    // Özel profilleri ekle
    this.customConfigSubject.value.forEach((config, name) => {
      profiles.push({ name: `CUSTOM_${name}`, config });
    });

    return profiles;
  }

  /**
   * Ayarları sıfırla
   */
  reset(): void {
    this.applyProfile('DEFAULT');
    this.customConfigSubject.next(new Map());
    this.saveConfig();
  }

  /**
   * Yapılandırmayı doğrula
   */
  private validateConfig(config: RaycastConfig): void {
    Object.keys(config).forEach(key => {
      const value = (config as any)[key];
      if (typeof value !== 'number' || value < 0 || value > 10) {
        // Invalid threshold value, validation warning handled silently
      }
    });
  }

  /**
   * LocalStorage'e kaydet
   */
  private saveConfig(): void {
    const data = {
      currentProfile: this.currentProfileSubject.value,
      currentConfig: this.configSubject.value,
      customConfigs: Array.from(this.customConfigSubject.value.entries())
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
  }

  /**
   * LocalStorage'den yükle
   */
  private loadConfig(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const data = JSON.parse(stored);

        // Özel profilleri yükle
        if (data.customConfigs && Array.isArray(data.customConfigs)) {
          const customConfigs = new Map<string, RaycastConfig>(data.customConfigs);
          this.customConfigSubject.next(customConfigs);
        }

        // Mevcut profili ve yapılandırmayı yükle
        if (data.currentConfig) {
          this.configSubject.next(data.currentConfig);
        }
        if (data.currentProfile) {
          this.currentProfileSubject.next(data.currentProfile);
        }
      } catch (e) {
        // Config load error, applying default profile
        this.applyProfile('DEFAULT');
      }
    }
  }
}
